import React, { useEffect, useState } from 'react';
import {
    Button,
    Dropdown,
    Form,
    Header,
    Icon,
    Label,
    Modal,
    Pagination,
    Segment,
    Table,
} from 'semantic-ui-react';
import moment from 'moment';
import { toast } from 'react-toastify';
import {
    EProgrammingStatus,
    Programming,
    ProgrammingFilter,
    statusOptions,
} from '../../models/programming';
import { ShortDto } from '../../models/short-dto';
import useProgrammingService from '../../services/backoffice/hooks/useProgrammingService';
import useReferenceService from '../../services/backoffice/hooks/useReferenceService';
import { ToasterConfig } from '../../config/ToasterConfig';
import {
    formatProgrammingDateTime,
    getCompactStageColumns,
} from '../../utils/programming-timeline-utils';

interface DriverProgrammingsModalProps {
    open: boolean;
    onClose: () => void;
    driverId: string;
    driverName: string;
    defaultStartDate?: string;
    defaultEndDate?: string;
}

const DriverProgrammingsModal: React.FC<DriverProgrammingsModalProps> = ({
    open,
    onClose,
    driverId,
    driverName,
    defaultStartDate,
    defaultEndDate,
}) => {
    const { getProgrammings } = useProgrammingService();
    const { getReferencesShort } = useReferenceService();
    const [programmings, setProgrammings] = useState<Programming[]>([]);
    const [references, setReferences] = useState<ShortDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState<ProgrammingFilter>({
        driverId,
        page: 1,
        perPage: 10,
        startDate: defaultStartDate,
        endDate: defaultEndDate,
    });
    const [totalPages, setTotalPages] = useState(0);

    useEffect(() => {
        if (!open) return;
        getReferencesShort()
            .then(setReferences)
            .catch(() => setReferences([]));
    }, [open, getReferencesShort]);

    useEffect(() => {
        if (!open || !driverId) return;

        setFilter(prev => ({
            ...prev,
            driverId,
            startDate: defaultStartDate ?? prev.startDate,
            endDate: defaultEndDate ?? prev.endDate,
            page: 1,
        }));
    }, [open, driverId, defaultStartDate, defaultEndDate]);

    useEffect(() => {
        if (!open || !driverId) return;

        const loadProgrammings = async () => {
            try {
                setLoading(true);
                const response = await getProgrammings({ ...filter, driverId });
                setProgrammings(response.data.itens ?? []);
                setTotalPages(response.data.totalPages ?? 0);
            } catch {
                toast.warn('Falha ao listar programações do motorista', ToasterConfig.error);
                setProgrammings([]);
                setTotalPages(0);
            } finally {
                setLoading(false);
            }
        };

        loadProgrammings();
    }, [open, driverId, filter, getProgrammings]);

    const handleFilterChange = (name: keyof ProgrammingFilter, value: unknown) => {
        setFilter(prev => ({ ...prev, [name]: value, page: 1 }));
    };

    const getStatusLabel = (status: string | number): string => {
        let statusValue: EProgrammingStatus | undefined;
        if (typeof status === 'string' && status in EProgrammingStatus) {
            statusValue = EProgrammingStatus[status as keyof typeof EProgrammingStatus];
        } else if (typeof status === 'number') {
            statusValue = status;
        }
        const found = statusOptions.find(opt => opt.value === statusValue);
        return found ? found.text : 'Desconhecido';
    };

    return (
        <Modal open={open} onClose={onClose} size="large" closeIcon>
            <Modal.Header>
                <Icon name="calendar alternate outline" />
                Programações — {driverName}
            </Modal.Header>
            <Modal.Content scrolling>
                <Segment>
                    <Form loading={loading}>
                        <Form.Group widths="equal">
                            <Form.Field>
                                <label>Data início</label>
                                <Form.Input
                                    type="datetime-local"
                                    value={filter.startDate ? moment(filter.startDate).format('YYYY-MM-DDTHH:mm') : ''}
                                    onChange={(_, { value }) => handleFilterChange('startDate', value ? new Date(value).toISOString() : undefined)}
                                />
                            </Form.Field>
                            <Form.Field>
                                <label>Data fim</label>
                                <Form.Input
                                    type="datetime-local"
                                    value={filter.endDate ? moment(filter.endDate).format('YYYY-MM-DDTHH:mm') : ''}
                                    onChange={(_, { value }) => handleFilterChange('endDate', value ? new Date(value).toISOString() : undefined)}
                                />
                            </Form.Field>
                            <Form.Field>
                                <label>Origem</label>
                                <Dropdown
                                    placeholder="Todas"
                                    fluid
                                    search
                                    clearable
                                    selection
                                    options={references}
                                    value={filter.originReferenceId || ''}
                                    onChange={(_, { value }) => handleFilterChange('originReferenceId', value || undefined)}
                                />
                            </Form.Field>
                            <Form.Field>
                                <label>Destino</label>
                                <Dropdown
                                    placeholder="Todos"
                                    fluid
                                    search
                                    clearable
                                    selection
                                    options={references}
                                    value={filter.destinationReferenceId || ''}
                                    onChange={(_, { value }) => handleFilterChange('destinationReferenceId', value || undefined)}
                                />
                            </Form.Field>
                        </Form.Group>
                    </Form>
                </Segment>

                <Table celled striped compact color="green">
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Placa CM</Table.HeaderCell>
                            <Table.HeaderCell>Rota</Table.HeaderCell>
                            <Table.HeaderCell>Saída prev. origem</Table.HeaderCell>
                            <Table.HeaderCell>Chegada prev. destino</Table.HeaderCell>
                            <Table.HeaderCell>Saída real origem</Table.HeaderCell>
                            <Table.HeaderCell>Chegada real destino</Table.HeaderCell>
                            <Table.HeaderCell>Status</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {programmings.length === 0 ? (
                            <Table.Row>
                                <Table.Cell colSpan="7" textAlign="center">
                                    Nenhuma programação encontrada
                                </Table.Cell>
                            </Table.Row>
                        ) : (
                            programmings.map(programming => {
                                const columns = getCompactStageColumns(programming);
                                return (
                                    <Table.Row key={programming.id}>
                                        <Table.Cell>{programming.mainVehiclePlateName || '—'}</Table.Cell>
                                        <Table.Cell>
                                            {`${programming.originReferenceName} → ${programming.destinationReferenceName}`}
                                        </Table.Cell>
                                        <Table.Cell>{columns.expectedOriginDeparture}</Table.Cell>
                                        <Table.Cell>{columns.expectedDestinationArrival}</Table.Cell>
                                        <Table.Cell>{columns.actualOriginDeparture}</Table.Cell>
                                        <Table.Cell>{columns.actualDestinationArrival}</Table.Cell>
                                        <Table.Cell>{getStatusLabel(programming.status)}</Table.Cell>
                                    </Table.Row>
                                );
                            })
                        )}
                    </Table.Body>
                </Table>

                {totalPages > 1 && (
                    <Pagination
                        activePage={filter.page}
                        totalPages={totalPages}
                        onPageChange={(_, { activePage }) => setFilter(prev => ({ ...prev, page: activePage as number }))}
                        size="small"
                    />
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose}>Fechar</Button>
            </Modal.Actions>
        </Modal>
    );
};

export default DriverProgrammingsModal;
